import React from 'react';
import {Form} from 'formik';
import classnames from 'classnames';
import FieldInput from 'common/components/FieldInput';
import ButtonsSubmitCancel from '../ButtonsSubmitCancel';
import FormTitle from '../FormTitle';
import FieldTextareaAutosize from '../../../../common/components/FieldTextareaAutosize';
import FieldSelect from '../../../../common/components/FieldSelect';
import MultipleTooltip from '../../../../common/components/MultipleTooltip';
import UploadingFilePopUp from '../../../../common/components/UploadingFilePopUp/UploadingFilePopUp';
import MarkFileUploaded from '../../../../common/components/MarkFileUploaded';
import FieldPasteFile from '../../../../common/components/FieldPasteFile';
import FieldSelectMulti from '../../../../common/components/FieldSelectMulti';
import ButtonTextSuggestions from '../../../../common/components/ButtonTextSuggestions';
import ModalSharedFilesList from '../../../../common/components/ModalSharedFilesList';
import FieldPasteImage from "../../../../common/components/FieldPasteImage";
import FieldCheckbox from "../../../../common/components/FieldCheckbox";
import ListUploadedFilesViewDelete from "../ListUploadedFilesViewDelete";
import {renovationCostDesc, selfBuildDesc, taxAndCostDesc} from '../../constants/textSuggestions';
import {getStep12Title} from '../../utils/getStep12Title';
import {divideThousandsIntegerValue, formatStringToNumber} from '../../../../common/utils/formatting';
import {limitValueRangeFloat} from '../../../../common/utils/form/limitValueLength';
import styles from './styles.module.scss';

const Form_12View = ({
                       values,
                       stepID,
                       shumaID,
                       setFieldValue,
                       shumaTypeID,
                       prevPagePath,
                       nextPagePath,
                       options,
                       progress,
                       showUploadingFilePopUp,
                       fileUploaded,
                       onFileSelected,
                       fileTypeIds,
                       fileSelectOptions: {
                         buildCostEstimate,
                         renovationCostEstimate,
                       },
                       filesListData
                     }) => {
  let finalMarketPrice = values.finalMarketPrice ? parseFloat(values.finalMarketPrice).toFixed(2) : 0;
  let deductionAmount = values.deductionAmount ? parseFloat(values.deductionAmount) : 0;
  const quickRealizeDeductionSum = (shumaTypeID === 3 || shumaTypeID === 4)
    ? ((finalMarketPrice - deductionAmount) * (values.quickRealizeDeduction) / 100).toFixed(2)
    : (finalMarketPrice * (values.quickRealizeDeduction) / 100).toFixed(2);
  const priceWithoutDeduction = (shumaTypeID === 3 || shumaTypeID === 4)
    ? (parseFloat(quickRealizeDeductionSum) || 0) + deductionAmount
    : (parseFloat(quickRealizeDeductionSum) || 0);
  const realizeCostSum = (finalMarketPrice * (values.realizeCost) / 100).toFixed(2);
  const taxesSum = (
    priceWithoutDeduction +
    (parseFloat(realizeCostSum) || 0) +
    (parseFloat(values.improvementLevy) || 0) +
    (parseFloat(values.bettermentTax) || 0) +
    (parseFloat(values.ramiCost) || 0)
  ).toFixed(2);
  const diffPriceTaxes = (finalMarketPrice - taxesSum).toFixed(2);
  const diffPriceQuickRealizeDeductionSum = (shumaTypeID === 3 || shumaTypeID === 4)
    ? (finalMarketPrice - quickRealizeDeductionSum - deductionAmount).toFixed(2)
    : (finalMarketPrice - quickRealizeDeductionSum).toFixed(2);
  const propertyPriceMinusDepreciation = (values.finalMarketPrice - values.deductionAmount).toFixed(2);
  const condition = values.propertyIdentifiedBy
    ? (values.propertyIdentifiedBy.map(item => item.value)).join()
    : '';

  return (
    <div className={styles.form_12}>
      <FormTitle title={getStep12Title(shumaTypeID)} className={styles.marginBottom25}/>
      <Form>
        <div className={styles.unit_1}>
          <h3 className={classnames(styles.subHeader, styles.marginBottom25)}>תחשיב</h3>
          <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
            <div className={styles.sideLabel}>
              {shumaTypeID === 2
                  ? <span>אומדן שווי מתואם</span>
                  : <span>הצעת אומדן שווי תחשיב</span>
              }
            </div>
            <FieldInput
              dir={'ltr'}
              ltrDirection={true}
              fieldId="estimatePriceFinal"
              value={divideThousandsIntegerValue(values.estimatePriceFinal)}
              disabled
              containerCustomStyles={styles.halfField}
            />
          </div>
          <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
            <div className={styles.sideLabel}>
              <span className={styles.mandatoryMark}>*</span>
              {
                shumaTypeID === 2
                  ? <span>שווי שוק סופי</span>
                  : <span>שווי שוק של הנכס</span>
              }
            </div>
            <FieldInput
              dir={'ltr'}
              ltrDirection={true}
              fieldId="finalMarketPrice"
              value={divideThousandsIntegerValue(values.finalMarketPrice)}
              onChange={formatStringToNumber}
              containerCustomStyles={styles.halfField}
            />
          </div>
          {(shumaTypeID !== 3 && shumaTypeID !== 4) && (
            <>
              <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
                <div className={styles.sideLabel}>
                  {(shumaTypeID !== 3 && shumaTypeID !== 4) && <span className={styles.mandatoryMark}>*</span>}
                  אומדן עלות בניה של נכס (ערך כינון)
                </div>
                <FieldInput
                  dir={'ltr'}
                  ltrDirection={true}
                  fieldId="propertyBuildCostEstimate"
                  value={divideThousandsIntegerValue(values.propertyBuildCostEstimate)}
                  onChange={formatStringToNumber}
                  containerCustomStyles={styles.halfField}
                />
              </div>
              <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
                <div className={styles.sideLabel}>
                  {(shumaTypeID !== 3 && shumaTypeID !== 4) && <span className={styles.mandatoryMark}>*</span>}
                  אומדן יתרת שווי הקרקע של הנכס
                </div>
                <FieldInput
                  dir={'ltr'}
                  ltrDirection={true}
                  fieldId="propertyLandRemainingValueEstimate"
                  value={divideThousandsIntegerValue(values.propertyLandRemainingValueEstimate)}
                  onChange={formatStringToNumber}
                  containerCustomStyles={styles.halfField}
                />
              </div>
            </>
          )}
          <h3 className={classnames(styles.subHeader, styles.marginBottom25)}>הפחתות</h3>
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={styles.sideLabel}>הפחתות</div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="deductionAmount"
                value={divideThousandsIntegerValue(values.deductionAmount)}
                onChange={formatStringToNumber}
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={styles.sideLabel}>שווי הנכס בניכוי הפחתות</div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="propertyPriceMinusDepreciation"
                value={divideThousandsIntegerValue(propertyPriceMinusDepreciation)}
                disabled
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
            <div className={classnames(styles.sideLabel, styles.thirdField)}>
              <span className={styles.mandatoryMark}>*</span>
              הפחתה לצורך מימוש מהיר
            </div>
            <FieldInput
              dir={'ltr'}
              ltrDirection={true}
              fieldId="quickRealizeDeduction"
              value={values.quickRealizeDeduction}
              showPercentIcon={true}
              onChange={limitValueRangeFloat(5, 0, 99.99)}
              containerCustomStyles={styles.smallField}
            />
            <FieldInput
              dir={'ltr'}
              ltrDirection={true}
              fieldId="quickRealizeDeductionSum"
              value={divideThousandsIntegerValue(quickRealizeDeductionSum)}
              disabled
              containerCustomStyles={styles.halfField}
            />
          </div>
          {(shumaTypeID === 2) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={classnames(styles.sideLabel, styles.thirdField)}>
                הוצאות מימוש (עו"ד,תיווך ועוד)
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="realizeCost"
                value={values.realizeCost}
                showPercentIcon={true}
                onChange={limitValueRangeFloat(5, 0, 99.99)}
                containerCustomStyles={styles.smallField}
              />
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="realizeCostSum"
                value={divideThousandsIntegerValue(realizeCostSum)}
                disabled
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {(shumaTypeID === 2) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={classnames(styles.sideLabel, styles.quarterField)}>
                היטל השבחה
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="improvementLevy"
                value={divideThousandsIntegerValue(values.improvementLevy)}
                onChange={formatStringToNumber}
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {(shumaTypeID === 2) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={classnames(styles.sideLabel, styles.quarterField)}>
                מס שבח
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="bettermentTax"
                value={divideThousandsIntegerValue(values.bettermentTax)}
                onChange={formatStringToNumber}
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {(shumaTypeID === 2) && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={classnames(styles.sideLabel, styles.quarterField)}>
                דמי היתר לרמ"י
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="ramiCost"
                value={divideThousandsIntegerValue(values.ramiCost)}
                onChange={formatStringToNumber}
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {shumaTypeID === 2 && (
            <div className={classnames(styles.marginBottomHigh, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={styles.sideLabel}>
                סה"כ הפחתות
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="taxesSum"
                value={divideThousandsIntegerValue(taxesSum)}
                disabled
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          {shumaTypeID === 2 && (
            <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
              <div className={styles.sideLabel}>
                תמורה נטו במימוש, במעוגל
              </div>
              <FieldInput
                dir={'ltr'}
                ltrDirection={true}
                fieldId="diffPriceTaxes"
                value={divideThousandsIntegerValue(diffPriceTaxes)}
                disabled
                containerCustomStyles={styles.halfField}
              />
            </div>
          )}
          <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
            <div className={styles.sideLabel}>
              שווי למימוש מהיר/כפוי
            </div>
            <FieldInput
              dir={'ltr'}
              ltrDirection={true}
              fieldId="diffPriceQuickRealizeDeductionSum"
              value={divideThousandsIntegerValue(diffPriceQuickRealizeDeductionSum)}
              disabled
              containerCustomStyles={styles.halfField}
            />
          </div>
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <>
              <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
                <div className={styles.sideLabel}>
                  {(shumaTypeID !== 3 && shumaTypeID !== 4) && <span className={styles.mandatoryMark}>*</span>}
                  אומדן עלות בניה של נכס (ערך כינון)
                </div>
                <FieldInput
                  dir={'ltr'}
                  ltrDirection={true}
                  fieldId="propertyBuildCostEstimate"
                  value={divideThousandsIntegerValue(values.propertyBuildCostEstimate)}
                  onChange={formatStringToNumber}
                  containerCustomStyles={styles.halfField}
                />
              </div>
              <div className={classnames(styles.marginBottom25, styles.flexBetween, styles.alignItemsCenter)}>
                <div className={styles.sideLabel}>
                  {(shumaTypeID !== 3 && shumaTypeID !== 4) && <span className={styles.mandatoryMark}>*</span>}
                  אומדן יתרת שווי הקרקע של הנכס
                </div>
                <FieldInput
                  dir={'ltr'}
                  ltrDirection={true}
                  fieldId="propertyLandRemainingValueEstimate"
                  value={divideThousandsIntegerValue(values.propertyLandRemainingValueEstimate)}
                  onChange={formatStringToNumber}
                  containerCustomStyles={styles.halfField}
                />
              </div>
            </>
          )}
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <FieldInput
              fieldId="taxAndCostDesc"
              value={values.taxAndCostDesc}
              label={
                <div>
                  <span>מיסים והיטלים</span>
                  <ButtonTextSuggestions
                    fieldName={'taxAndCostDesc'}
                    currentFieldValue={values.taxAndCostDesc}
                    textSuggestionsArray={taxAndCostDesc}
                    setFieldValue={setFieldValue}
                  />
                </div>
              }
              containerCustomStyles={styles.marginBottom25}
            />
          )}
          <FieldTextareaAutosize
            fieldId="calculationNotes"
            value={values.calculationNotes}
            label={'הערות לתחשיב'}
          />
        </div>
        <div className={classnames(styles.unit_2, styles.marginBottomHigh)}>
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <div>
              <FieldSelect
                fieldId="propertyLoanLegalRelation"
                options={options.propertyLoanLegalRelationOptions}
                label={'הקשר המשפטי בין הלווה לבין הנכס'}
                containerStyles={classnames(styles.halfField, styles.marginBottom25)}
              />
              {(values.propertyLoanLegalRelation && values.propertyLoanLegalRelation.value === 4) &&
                <FieldTextareaAutosize
                  fieldId="propertyLoanLegalRelationText"
                  value={values.propertyLoanLegalRelationText}
                  label="פירוט הקשר המשפטי"
                  containerCustomStyles={styles.marginBottom25}
                />
              }
              {(values.loanToPropertyBond && values.loanToPropertyBond.value === 4) &&
                <FieldInput
                  fieldId="loanToPropertyBondText"
                  value={values.loanToPropertyBondText}
                  mandatoryMark={true}
                  label={'פירוט הקשר המשפטי בין הלווה לבין הנכס'}
                  containerCustomStyles={styles.marginBottom25}
                />
              }
              {(values.buildingMatchSurroundings && values.buildingMatchSurroundings.value === 2) &&
                <FieldInput
                  fieldId="buildingMatchSurroundingsText"
                  value={values.buildingMatchSurroundingsText}
                  mandatoryMark={true}
                  label={'פירוט הבניין לא תואם את סביבתו'}
                  containerCustomStyles={styles.marginBottom25}
                />
              }
            </div>
          )}
          <FieldTextareaAutosize
            fieldId="mortgageEntityEmphasis"
            value={values.mortgageEntityEmphasis}
            label={
              <div className={styles.flex}>
                <span className={styles.marginLeftHalf}>הדגשים לגוף המשעבד</span>
                <MultipleTooltip
                  id={'testTooltip'}
                  label={'בנייה ללא היתר/ חריגות בנייה/ חריגות כגון סיפוח גינה, גג וכיו"ב".'}
                />
              </div>
            }
            containerCustomStyles={styles.marginBottom25}
          />
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <>
              <FieldSelectMulti
                fieldId="propertyIdentifiedBy"
                placeholder="בחר"
                options={options.propertyIdentifiedByOptions}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isClearable={false}
                label={'זיהוי הנכס בוצע באמצעות'}
                containerStyles={styles.marginBottom25}
              />
              {condition.includes('25') &&
                <FieldTextareaAutosize
                  fieldId="propertyIdentifiedByText"
                  value={values.propertyIdentifiedByText}
                  mandatoryMark={true}
                  label={'פירוט זיהוי הנכס בוצע באמצעות'}
                  containerCustomStyles={styles.marginBottom25}
                />
              }
            </>
          )}
          {(shumaTypeID === 3 || shumaTypeID === 4) && (
            <>
              <FieldTextareaAutosize
                fieldId="selfBuildDesc"
                value={values.selfBuildDesc}
                label={
                  <div>
                    <span>פירוט בניה עצמית</span>
                    <ButtonTextSuggestions
                      fieldName={'selfBuildDesc'}
                      currentFieldValue={values.selfBuildDesc}
                      textSuggestionsArray={selfBuildDesc}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                }
                containerCustomStyles={styles.marginBottom25}
              />
              <h4 className={classnames(styles.subHeader, styles.marginBottom25)}>אומדן עלויות הבניה</h4>
              <FieldCheckbox fieldId={'fileType34Switch'} />
              <div className={classnames(
                styles.flex,
                styles.marginBottom25,
                styles.alignItemsCenter,
                styles.marginTopLow
              )}>
                <ModalSharedFilesList file={buildCostEstimate} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.addFileButton}>
                  {!values.fileType34Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'buildCostEstimateFileUploaded'}
                        fileUploaded={fileUploaded.buildCostEstimateFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.buildCostEstimate)}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'buildCostEstimateFileUploaded'}
                        fileUploaded={fileUploaded.buildCostEstimateFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.buildCostEstimate)}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                  {(fileUploaded.buildCostEstimateFileUploaded) && <MarkFileUploaded/>}
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.buildCostEstimate}
                shumaId={shumaID}
              />
              <FieldTextareaAutosize
                fieldId="renovationDetailsDesc"
                value={values.renovationDetailsDesc}
                label={'פרוט מהות השיפוץ'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="renovationCostDesc"
                value={values.renovationCostDesc}
                label={
                  <div>
                    <span>פירוט עלויות ואומדן השיפוצים הצפויים</span>
                    <ButtonTextSuggestions
                      fieldName={'renovationCostDesc'}
                      currentFieldValue={values.renovationCostDesc}
                      textSuggestionsArray={renovationCostDesc}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                }
                containerCustomStyles={styles.marginBottom25}
              />
              <h4 className={classnames(styles.subHeader, styles.marginBottom25)}>
                אומדן עלות השיפוצים
              </h4>
              <FieldCheckbox fieldId={'fileType35Switch'} />
              <div className={classnames(
                styles.flex,
                styles.marginBottom25,
                styles.alignItemsCenter,
                styles.marginTopLow
              )}>
                <ModalSharedFilesList file={renovationCostEstimate} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.addFileButton}>
                  {!values.fileType35Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'renovationCostEstimateFileUploaded'}
                        fileUploaded={fileUploaded.renovationCostEstimateFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.renovationCostEstimate)}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'renovationCostEstimateFileUploaded'}
                        fileUploaded={fileUploaded.renovationCostEstimateFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.renovationCostEstimate)}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                  {(fileUploaded.renovationCostEstimateFileUploaded) && <MarkFileUploaded/>}
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.renovationCostEstimate}
                shumaId={shumaID}
              />
            </>
          )}
        </div>
        <ButtonsSubmitCancel prevPagePath={prevPagePath} nextPagePath={nextPagePath}/>
      </Form>
      <UploadingFilePopUp
        showUploadingFilePopUp={showUploadingFilePopUp}
        progress={progress}
        message="הקובץ נטען, אנא המתינו"
      />
    </div>
  );
};

export default Form_12View;